import { Edit, EditIcon, PlusIcon, TrashIcon } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useToast } from "../../hooks/use-toast";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";

interface ManageAccessPopoverProps {
  userId: string;
  onClose: () => void;
}

interface Role {
  id: number;
  name: string;
  description?: string;
}

interface AccessLevelItem {
  id: number;
  access_type: string;
  access_type_id: number;
}

const ManageAccessPopover: React.FC<ManageAccessPopoverProps> = ({
  userId,
  onClose,
}) => {
  const [userRole, setUserRole] = useState<{
    role_id: number;
    role_name: string;
    role_description?: string;
  } | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [providers, setProviders] = useState<{ id: number; name: string }[]>(
    [],
  );
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(
    null,
  );
  const [accessLevels, setAccessLevels] = useState<AccessLevelItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [allEntities, setAllEntities] = useState<any[]>([]);
  const [userEntityIds, setUserEntityIds] = useState<number[]>([]);
  const [entitySelection, setEntitySelection] = useState<number[]>([]);
  const [isEntityEditing, setIsEntityEditing] = useState(false);
  const [canDownloadExcel, setCanDownloadExcel] = useState(false);
  const [canUploadExcel, setCanUploadExcel] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
    loadAccessLevels();
    loadProviders();
  }, [userId]);

  const loadAccessLevels = async () => {
    try {
      const levels = await api.getAccessLevels(userId);
      if (levels?.results) {
        setAccessLevels(levels.results);
        const providerAccess = levels.results.find(
          (level: AccessLevelItem) => level.access_type === "provider",
        );
        if (providerAccess) {
          setSelectedProviderId(providerAccess.access_type_id);
        }
      }
    } catch (error) {
      console.error("Error loading access levels:", error);
    }
  };

  const loadProviders = async () => {
    try {
      const providersList = await api.getProviders();
      if (providersList) {
        setProviders(providersList);
      }
    } catch (error) {
      console.error("Error loading providers:", error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [roleResp, rolesResp, entitiesResp, userEntitiesResp] =
        await Promise.all([
          api.getUserRole(userId),
          api.getRoles(),
          api.getAllEntities(""),
          api.getUserEntities(userId),
        ]);

      if (roleResp?.success && roleResp.data) {
        setUserRole({
          role_id: roleResp.data.role_id,
          role_name: roleResp.data.role_name,
          role_description: roleResp.data.role_description,
        });
        setSelectedRoleId(roleResp.data.role_id);

        // Check if user's role is Super Admin by name
        const isSuperAdmin = roleResp.data.role_name === "Super Admin";

        if (isSuperAdmin) {
          // For Super Admin, default to true if not explicitly set to false
          setCanDownloadExcel(roleResp.data.can_download_excel ?? true);
          setCanUploadExcel(roleResp.data.can_upload_excel ?? true);
        } else {
          // For other roles, use the stored value or default to false
          setCanDownloadExcel(roleResp.data.can_download_excel ?? false);
          setCanUploadExcel(roleResp.data.can_upload_excel ?? false);
        }
      } else {
        setUserRole(null);
        setSelectedRoleId(null);
      }

      if (rolesResp?.success) {
        setRoles(rolesResp.data || []);
      }

      if (entitiesResp?.results) {
        setAllEntities(entitiesResp.results);
      }

      if (userEntitiesResp?.success && Array.isArray(userEntitiesResp.data)) {
        const ids = userEntitiesResp.data.map((e: any) => Number(e.entity_id));
        setUserEntityIds(ids);
      } else {
        setUserEntityIds([]);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-enable Excel permissions when Super Admin role is selected
  useEffect(() => {
    if (selectedRoleId) {
      const selectedRole = roles.find(r => r.id === selectedRoleId);
      if (selectedRole?.name === "Super Admin") {
        setCanDownloadExcel(true);
        setCanUploadExcel(true);
      }
    }
  }, [selectedRoleId, roles]);

  const handleAddOrUpdate = async () => {
    if (!selectedRoleId) {
      toast({
        title: "Error",
        description: "Please select a role",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await api.assignRoleToUser(
        userId,
        selectedRoleId,
        canDownloadExcel,
        canUploadExcel,
      );
      if (response?.success) {
        toast({
          title: "Success",
          description: `Role assigned successfully. User needs to refresh/login to see changes.`,
        });
        await loadData();
        setIsEditing(false);
        setSelectedRoleId(null);
      } else {
        toast({
          title: "Error",
          description: response?.error || "Failed to assign role",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error assigning role:", error);
      toast({
        title: "Error",
        description: "Failed to assign role",
        variant: "destructive",
      });
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    if (userRole) {
      setSelectedRoleId(userRole.role_id);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (userRole) {
      setSelectedRoleId(userRole.role_id);
    } else {
      setSelectedRoleId(null);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await api.removeRoleFromUser(userId);
      if (response?.success) {
        toast({
          title: "Success",
          description: "Role removed successfully",
        });
        await loadData();
      } else {
        toast({
          title: "Error",
          description: response?.error || "Failed to remove role",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error removing role:", error);
      toast({
        title: "Error",
        description: "Failed to remove role",
        variant: "destructive",
      });
    }
  };

  const handleDeleteAccessLevel = async (id: number) => {
    try {
      await api.deleteAccess(id);
      setAccessLevels(accessLevels.filter(level => level.id !== id));
      toast({
        title: "Access Level Removed",
        description: "Regional access level has been removed",
      });
    } catch (error) {
      console.error("Error deleting access level:", error);
      toast({
        title: "Error",
        description: "Failed to remove access level",
        variant: "destructive",
      });
    }
  };

  const handleProviderChange = async (providerId: string) => {
    const providerIdNum = providerId === "none" ? null : Number(providerId);
    const existingProviderAccess = accessLevels.find(
      level => level.access_type === "provider",
    );

    if (providerIdNum === null) {
      if (existingProviderAccess) {
        await api.deleteAccess(existingProviderAccess.id);
        setAccessLevels(
          accessLevels.filter(level => level.access_type !== "provider"),
        );
        toast({
          title: "Provider Access Removed",
          description: "User can now access all providers",
        });
      }
      setSelectedProviderId(null);
    } else {
      if (existingProviderAccess) {
        await api.updateAccess({
          id: existingProviderAccess.id,
          access_type: "provider",
          access_type_id: providerIdNum,
        });
        setAccessLevels(
          accessLevels.map(level =>
            level.access_type === "provider"
              ? { ...level, access_type_id: providerIdNum }
              : level,
          ),
        );
        toast({
          title: "Provider Updated",
          description: `User restricted to: ${providers.find(p => p.id === providerIdNum)?.name}`,
        });
      } else {
        const result = await api.createAccess({
          user_id: userId,
          access_type: "provider",
          access_type_id: providerIdNum,
        });
        setAccessLevels([
          ...accessLevels,
          {
            access_type: "provider",
            access_type_id: providerIdNum,
            id: result.id,
          },
        ]);
        toast({
          title: "Provider Assigned",
          description: `User restricted to: ${providers.find(p => p.id === providerIdNum)?.name}`,
        });
      }
      setSelectedProviderId(providerIdNum);
    }
  };

  const startEntityEdit = () => {
    setEntitySelection(userEntityIds);
    setIsEntityEditing(true);
  };

  const toggleEntitySelection = (id: number) => {
    const numericId = Number(id);
    setEntitySelection(prev =>
      prev.includes(numericId)
        ? prev.filter(x => x !== numericId)
        : [...prev, numericId],
    );
  };

  const handleSaveEntities = async () => {
    try {
      const response = await api.assignEntitiesToUser(userId, entitySelection);
      if (response?.success) {
        toast({
          title: "Success",
          description:
            "Entity access updated successfully. User needs to refresh/login to see changes.",
        });
        await loadData();
        setIsEntityEditing(false);
      } else {
        toast({
          title: "Error",
          description: response?.error || "Failed to update entity access",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating entities:", error);
      toast({
        title: "Error",
        description: "Failed to update entity access",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent>
          <DialogTitle>Manage Access Levels</DialogTitle>
          <div className="text-center py-4">Loading...</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogTitle className="flex items-center justify-between mt-4">
          <span>Manage Access Levels</span>
          {!userRole && !isEditing && (
            <Button
              onClick={() => setIsEditing(true)}
              className="ml-2 bg-white text-black hover:text-white border dark:hover:bg-gray-600 border-gray-300 dark:bg-black dark:text-white"
            >
              <span className="mr-2">Add Role</span>
              <PlusIcon className="w-4 h-4" />
            </Button>
          )}
        </DialogTitle>

        {/* Provider Access Section */}
        <div className="mb-4 p-4 border rounded-lg bg-gray-50 dark:bg-gray-800">
          <label className="block text-sm font-medium mb-2">
            Assign Provider (Optional)
          </label>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
            When a provider is assigned, this user will ONLY see data from that
            provider across the entire ops panel.
          </p>
          <Select
            value={selectedProviderId?.toString() || "none"}
            onValueChange={handleProviderChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None (Access All Providers)</SelectItem>
              {providers.map(provider => (
                <SelectItem key={provider.id} value={provider.id.toString()}>
                  {provider.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="border-t pt-4 mb-2">
          <h3 className="text-sm font-medium">Role Assignment</h3>
        </div>

        {userRole && !isEditing ? (
          <div className="flex flex-col space-y-6">
            <div className="flex flex-col items-center">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Role</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">
                      {userRole.role_name}
                    </TableCell>
                    <TableCell>{userRole.role_description || "-"}</TableCell>
                    <TableCell>
                      <Button
                        onClick={handleEdit}
                        className="bg-white text-black hover:text-white dark:hover:bg-gray-600 border hover:bg-gray-100 border-gray-300 dark:bg-black dark:text-white"
                      >
                        <EditIcon className="w-4 h-4 mr-2" /> Edit
                      </Button>
                      <Button
                        onClick={handleDelete}
                        className="ml-2 bg-red-500 text-white border border-gray-300 dark:bg-red-800 dark:text-white"
                      >
                        <TrashIcon className="w-4 h-4 mr-2" /> Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            <div className="border rounded-md p-4 w-full">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">Entity Access</h3>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={startEntityEdit}
                  className="bg-white text-black hover:text-white dark:hover:bg-gray-600 border-gray-300 dark:bg-black dark:text-white"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Entities
                </Button>
              </div>
              {userEntityIds.length === 0 ? (
                <p className="text-sm text-gray-500 bg-gray-50 dark:bg-gray-800 p-3 rounded-md border border-dashed text-center">
                  No entities assigned. User will not see any pharmacies by
                  default.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2 mt-2">
                  {allEntities
                    .filter(e => userEntityIds.includes(e.id))
                    .map(e => (
                      <Badge
                        key={e.id}
                        variant="secondary"
                        className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100 border-none"
                      >
                        {e.name}
                        {e.branch_id ? ` (${e.branch_id})` : ""}
                      </Badge>
                    ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-4">No role assigned</div>
        )}

        {isEditing && (
          <div className="flex flex-col items-center mt-4">
            <div className="mb-2 flex justify-between w-full space-x-2">
              <Select
                value={selectedRoleId?.toString() || ""}
                onValueChange={value =>
                  setSelectedRoleId(Number.parseInt(value))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(role => (
                    <SelectItem key={role.id} value={role.id.toString()}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Excel Permissions Section */}
            <div className="w-full border rounded-md p-4 mb-4 space-y-3">
              <h4 className="text-sm font-semibold mb-2">Excel Permissions</h4>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label
                    htmlFor="can-download-excel"
                    className="text-sm font-medium"
                  >
                    Allow Excel Download
                  </Label>
                  <p className="text-xs text-gray-500">
                    User can download Excel files from the application
                  </p>
                </div>
                <Switch
                  id="can-download-excel"
                  checked={canDownloadExcel}
                  onCheckedChange={setCanDownloadExcel}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label
                    htmlFor="can-upload-excel"
                    className="text-sm font-medium"
                  >
                    Allow Excel Upload
                  </Label>
                  <p className="text-xs text-gray-500">
                    User can upload Excel files to the application
                  </p>
                </div>
                <Switch
                  id="can-upload-excel"
                  checked={canUploadExcel}
                  onCheckedChange={setCanUploadExcel}
                />
              </div>
            </div>

            <div className="flex items-center w-full">
              <Button
                onClick={handleAddOrUpdate}
                className="mb-2 bg-white text-black hover:text-white border dark:hover:bg-gray-600 border-gray-300 dark:bg-black dark:text-white"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                {userRole ? "Update" : "Add"}
              </Button>
              <Button
                onClick={handleCancel}
                className="mb-2 ml-2 bg-red-500 text-white border border-gray-300 dark:bg-red-800 dark:text-white"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {isEntityEditing && (
          <div className="mt-6 border-t pt-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex flex-col">
                <h3 className="font-semibold">Select Entities</h3>
                <span className="text-xs text-gray-500">
                  {entitySelection.length} selected
                </span>
              </div>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEntitySelection(allEntities.map(e => Number(e.id)))}
                  className="text-xs"
                >
                  Select All
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEntitySelection([])}
                  className="text-xs"
                >
                  Deselect All
                </Button>
              </div>
            </div>
            <div className="max-h-60 overflow-y-auto space-y-2 border rounded-md p-3">
              {allEntities.map(entity => (
                <div
                  key={entity.id}
                  className={`flex items-center space-x-2 py-1.5 rounded px-2 transition-colors ${
                    entitySelection.includes(Number(entity.id))
                      ? "bg-blue-50 dark:bg-blue-900/20"
                      : "hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  <Checkbox
                    id={`entity-${entity.id}`}
                    checked={entitySelection.includes(Number(entity.id))}
                    onCheckedChange={() => toggleEntitySelection(entity.id)}
                  />
                  <Label
                    htmlFor={`entity-${entity.id}`}
                    className="text-sm cursor-pointer flex-1"
                  >
                    {entity.name}
                    {entity.branch_id ? ` (${entity.branch_id})` : ""}
                  </Label>
                </div>
              ))}
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setIsEntityEditing(false)}
                className="bg-white text-black hover:text-white dark:hover:bg-gray-600 border-gray-300 dark:bg-black dark:text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEntities}
                className="bg-white text-black hover:text-white dark:hover:bg-gray-600 border-gray-300 dark:bg-black dark:text-white"
              >
                Save Entities
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ManageAccessPopover;
